import { Box, CircularProgress } from '@material-ui/core';
import { includes, isEmpty, map } from 'lodash';
import { useObserver } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FB, FBEditorState, FBFieldName, FBSchemaComponentProps, FBSchemaState, FBSortable, FBWorkspaceModeOptions } from '..';
import SM from '../../../App/Shifamed/Utils/SM/SM';
import { intl } from '../../../common/intl';
import { formFieldsDisabledChangeControl, suppliersDisabledChangeControl } from '../../document.revision/utils/helpers';
import { isTranslation } from '../../translations/types';
import FBFieldOutputWrapper from '../FBFieldOutputWrapper/FBFieldOutputWrapper';

const loaderRenderer = () => (
  <Box display="flex" justifyContent="center">
    <CircularProgress size={40} disableShrink />
  </Box>
);

export const withFBSchema = <T extends FBSchemaComponentProps>(
  Component: React.FC<T>,
): React.FC<T> => {
  const Comp = ({
    workspaceMode,
    schemaState,
    schema,
    ...props
  }: T) => {
    !schemaState && (schemaState = FB.useRef<FBSchemaState>(FBSchemaState, schema));
    const { workspaceState, formState } = FB.useStores();
    const { isOutput, id, document: { outputRevision = false } = {} } = workspaceState || {};
    const { _tabsState, _documentRevisionFormState } = SM.useStores();
    const isBuildTypeChangeApprovalEnabled = useObserver(() => workspaceState?.buildTypeChangeStatus());

    // To avoid UI-freezes on rendering huge list in schema.
    const [rendered, setRendered] = useState(false);
    useEffect(() => {
      setTimeout(() => setRendered(true));
    }, []);

    // One O(n) forward pass over the schema producing, per position:
    //  - approvalBlockAt[i]: is position i gated by the nearest preceding
    //    (in)approval field that is blocking and unfilled
    //  - stepIndexAt[i]: running count of non-deleted step-index-type items in [0..i]
    // Replaces the per-item backward scan in isApproved() and the per-item
    // slice+filter for stepIndex, both of which were O(n²) across the schema.
    function buildSchemaMaps (): { approvalBlockAt: boolean[], stepIndexAt: number[] } {
      const approvalBlockAt: boolean[] = [];
      const stepIndexAt: number[] = [];
      let blocked = false;
      let steps = 0;
      (schema || []).forEach((item, i) => {
        approvalBlockAt[i] = blocked;
        const { type, name } = item;
        if (type === 'requestapproval' || type === 'inlineapproval') {
          const activeConfig = type === 'inlineapproval' ? item : formState?.getFieldValue(name);
          const { approveIsBlocking = false } = activeConfig || {};
          const hasValue = Boolean(type === 'inlineapproval'
            ? formState?.getFieldValue(name)
            : formState?.getFieldValue(FBFieldName.RequestApproval + name));
          blocked = approveIsBlocking && !hasValue;
        }
        if (!item.deleted && includes(FBEditorState.pickStepIndex, item.type)) {
          steps++;
        }
        stepIndexAt[i] = steps;
      });
      return { approvalBlockAt, stepIndexAt };
    }

    function schemaRenderer (): React.ReactNode | React.ReactNode[] | undefined {
      const isDesignOrFormDesign = [
        FBWorkspaceModeOptions.DESIGN,
        FBWorkspaceModeOptions.FORMDESIGN,
      ].includes(workspaceMode as FBWorkspaceModeOptions) && !workspaceState?.document?.isBeingEditedAfterRelease;

      const isPreviewOrFormPreview = [
        FBWorkspaceModeOptions.PREVIEW,
        FBWorkspaceModeOptions.FORMPREVIEW,
      ].includes(workspaceMode as FBWorkspaceModeOptions);

      schema?.forEach(({ name, validationAttribute, label, placeholder }) => {
        let attr = validationAttribute || (label as string) || placeholder || name;
        if (attr && isTranslation(attr)) {
          attr = intl.formatMessage({ id: attr });
        }
        if (name && attr) {
          formState?.setValidationAttributeName(name, attr);
        }
      });

      if (isDesignOrFormDesign) {
        return <FBSortable />;
      }

      const { approvalBlockAt, stepIndexAt } = buildSchemaMaps();
      let position = 0;
      return map(schema, (schemaItem, index) => {
        const isUnderWorkspace = workspaceState?.getSchemaItem(schemaItem.name);
        const { disabled, outputDisabled } = schemaItem || {};
        const reviseDisabled = schemaItem.editorProperties?.includes('reviseDisabled');
        if (schemaItem.deleted || schemaItem.hidden
          || ((_tabsState && schemaItem.tabId && !_tabsState?.isTabActive(schemaItem.tabId)
            && !_documentRevisionFormState?.leftPanelOpen))
        ) { return; }

        const disabledViaConfigOptions = schemaItem.name === 'part-vendors'
          ? suppliersDisabledChangeControl(workspaceState?.document) : formFieldsDisabledChangeControl(workspaceState?.document);

        position++;
        return (
          <FBFieldOutputWrapper position={position} inputProps={schemaItem} key={schemaItem.name}>
            {FBSchemaState.component({
              autosave: isUnderWorkspace && workspaceState?.autosave,
              ...schemaItem,
              ...(includes(FBEditorState.pickStepIndex, schemaItem.type)) && {
                stepIndex: stepIndexAt[index],
              },
              disabled: (
                disabled
                || disabledViaConfigOptions
                || isPreviewOrFormPreview
                || approvalBlockAt[index]
                || (reviseDisabled && outputRevision)
                || (outputDisabled && isOutput && !isEmpty(id))
                || isBuildTypeChangeApprovalEnabled
              ),
              gutter: false,
              index,
            })}
          </FBFieldOutputWrapper>
        );
      });
    }

    useObserver(() => {
      schema = schemaState?.schema;
    });

    return Component({
      ...(props as T),
      schemaState,
      workspaceMode,
      schema,
      children: rendered ? schemaRenderer() : loaderRenderer(),
    });
  };

  return (props: T) => Comp(props);
};
